// services/geminiService.ts
import { HappyHourDeal, DayOfWeek } from "../types";

const SCAN_MENU_ENDPOINT = "/.netlify/functions/scan-menu";

// Accept day names plus the server's normalized numeric values.
function normalizeDays(raw: any): DayOfWeek[] {
  if (!Array.isArray(raw)) return [];

  const map: Record<string, DayOfWeek> = {
    sunday: DayOfWeek.SUNDAY, sun: DayOfWeek.SUNDAY, "0": DayOfWeek.SUNDAY, "7": DayOfWeek.SUNDAY,
    monday: DayOfWeek.MONDAY, mon: DayOfWeek.MONDAY, "1": DayOfWeek.MONDAY,
    tuesday: DayOfWeek.TUESDAY, tue: DayOfWeek.TUESDAY, "2": DayOfWeek.TUESDAY,
    wednesday: DayOfWeek.WEDNESDAY, wed: DayOfWeek.WEDNESDAY, "3": DayOfWeek.WEDNESDAY,
    thursday: DayOfWeek.THURSDAY, thu: DayOfWeek.THURSDAY, thur: DayOfWeek.THURSDAY, thurs: DayOfWeek.THURSDAY, "4": DayOfWeek.THURSDAY,
    friday: DayOfWeek.FRIDAY, fri: DayOfWeek.FRIDAY, "5": DayOfWeek.FRIDAY,
    saturday: DayOfWeek.SATURDAY, sat: DayOfWeek.SATURDAY, "6": DayOfWeek.SATURDAY,
  };

  const out: DayOfWeek[] = [];
  for (const d of raw) {
    const numeric = typeof d === "number" && Number.isFinite(d) ? String(d) : null;
    const key = String(numeric ?? d ?? "").trim().toLowerCase();
    const val = map[key];
    if (val) out.push(val);
  }
  return Array.from(new Set(out));
}

function normalizeDeals(raw: any): HappyHourDeal[] {
  const arr = Array.isArray(raw) ? raw : [];
  const now = Date.now();

  return arr
    .map((d: any, i: number) => ({
      id: d?.id || `ai-${now}-${i}`,
      item: String(d?.item ?? "").trim(),
      price: String(d?.price ?? "").trim(),
      category: String(d?.category ?? "Beer").trim(),
      isFeatured: i < 5,
      isGroupDeal: !!d?.isGroupDeal,
    }))
    .filter((d) => d.item.length > 0);
}

export const extractHappyHourFromImage = async (
  base64Image: string,
  mimeType: string
): Promise<{
  deals: HappyHourDeal[];
  startTime: string;
  endTime: string;
  days: DayOfWeek[];
}> => {
  const res = await fetch(SCAN_MENU_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      base64Image,
      mimeType: mimeType || "image/jpeg",
    }),
  });

  const text = await res.text();
  let json: any = null;

  try {
    json = JSON.parse(text);
  } catch {
    // ignore parse error here; handled below
  }

  if (!res.ok) {
    throw new Error(json?.error || json?.message || text || "Menu scan failed");
  }

  // Support either {data:{...}} or direct {...}
  const payload = json?.data ?? json;

  const normalized = {
    deals: normalizeDeals(payload?.deals),
    startTime: payload?.startTime || "16:00",
    endTime: payload?.endTime || "18:00",
    days: normalizeDays(payload?.days),
  };
  
  const hasAnyData =
    normalized.deals.length > 0 ||
    normalized.days.length > 0 ||
    Boolean(payload?.startTime) ||
    Boolean(payload?.endTime);

  if (!hasAnyData && (json?.warning || json?.details)) {
    throw new Error(json?.warning || json?.details || "Menu scan failed");
  }

  return normalized;
};
